/**
 * Single source of truth for per-page SEO: titles, descriptions, canonical
 * URLs and schema.org JSON-LD. Pure functions with no imports so the same
 * module runs in the React app and in the Node prerender script
 * (scripts/prerender.mjs).
 */

export const SITE_URL = "https://khharutyunyan.dev";

export const absoluteUrl = (path = "/") => (/^https?:\/\//.test(path) ? path : SITE_URL + path);

/** Calendar years since the career start (Dec 2018 → 8 in 2026), grows every January */
export const yearsOfExperience = (careerStart, now = new Date()) => now.getFullYear() - new Date(careerStart).getFullYear();

const KEYWORDS = [
  "Khachik Harutyunyan",
  "Хачик Арутюнян",
  "Խաչիկ Հարությունյան",
  "full-stack developer",
  "React developer",
  "React Native developer",
  "Next.js developer",
  "mobile app developer",
  "iOS and Android developer",
  "Node.js developer",
  "frontend developer",
  "web developer Armenia",
  "developer Yerevan",
  "hire React Native developer",
  "freelance developer",
];

const personId = `${SITE_URL}/#person`;
const websiteId = `${SITE_URL}/#website`;

const personNode = (profile, resume) => ({
  "@type": "Person",
  "@id": personId,
  name: profile.name,
  givenName: profile.givenName,
  familyName: profile.familyName,
  alternateName: profile.alternateNames,
  jobTitle: profile.title,
  description: `${profile.title} with ${yearsOfExperience(profile.careerStart)}+ years of experience building web and mobile apps with ${profile.coreStack.join(", ")}.`,
  url: SITE_URL,
  image: absoluteUrl(profile.image),
  email: `mailto:${profile.email}`,
  address: {
    "@type": "PostalAddress",
    addressLocality: profile.city,
    addressCountry: profile.country,
  },
  worksFor: { "@type": "Organization", name: profile.currentCompany },
  hasOccupation: {
    "@type": "Occupation",
    name: profile.title,
    occupationLocation: { "@type": "City", name: profile.city },
    skills: profile.coreStack.join(", "),
  },
  alumniOf: resume.education.map((e) => ({ "@type": "CollegeOrUniversity", name: e.school })),
  hasCredential: resume.certifications.map((c) => ({
    "@type": "EducationalOccupationalCredential",
    name: c.name,
    recognizedBy: { "@type": "Organization", name: c.issuer },
  })),
  knowsAbout: [
    "React",
    "React Native",
    "Next.js",
    "TypeScript",
    "JavaScript",
    "Node.js",
    "NestJS",
    "Vue.js",
    "GraphQL",
    "REST APIs",
    "PostgreSQL",
    "MongoDB",
    "Firebase",
    "iOS development",
    "Android development",
    "Mobile app development",
    "Web development",
    "Software architecture",
    "Technical leadership",
  ],
  knowsLanguage: profile.spokenLanguages.map((l) => ({ "@type": "Language", name: l.name, alternateName: l.code })),
  sameAs: profile.socials.map((s) => s.url),
});

const websiteNode = (profile) => ({
  "@type": "WebSite",
  "@id": websiteId,
  url: SITE_URL,
  name: `${profile.name} — ${profile.title}`,
  alternateName: profile.alternateNames,
  inLanguage: "en",
  publisher: { "@id": personId },
});

const breadcrumbs = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({
    "@type": "ListItem",
    position: i + 1,
    name,
    item: absoluteUrl(path),
  })),
});

const graph = (...nodes) => ({ "@context": "https://schema.org", "@graph": nodes });

export const projectPath = (project) => `/portfolio/${project.slug}`;

/**
 * Builds SEO metadata for every indexable route.
 * @returns {Array<{path, title, description, image, type, jsonLd, changefreq, priority}>}
 */
export function buildPages({ profile, projects, resume, home, ru }) {
  const years = yearsOfExperience(profile.careerStart);
  const base = () => [websiteNode(profile), personNode(profile, resume)];
  const image = profile.ogImage;

  const pages = [
    {
      path: "/",
      lang: "en",
      alternates: [["en", "/"], ["ru", "/ru"], ["x-default", "/"]],
      title: `${profile.name} — ${profile.title}`,
      description: `${profile.name} is a ${profile.title} from ${profile.location} with ${years}+ years of experience. I build fast iOS, Android and web apps with React, React Native, Next.js and Node.js. Available for freelance & remote work.`,
      image,
      type: "profile",
      changefreq: "monthly",
      priority: "1.0",
      jsonLd: graph(...base(), {
        "@type": "ProfilePage",
        "@id": `${SITE_URL}/#profilepage`,
        url: SITE_URL,
        name: `${profile.name} — ${profile.title}`,
        isPartOf: { "@id": websiteId },
        mainEntity: { "@id": personId },
        about: { "@id": personId },
        inLanguage: "en",
      }, ...(home?.faq?.length
        ? [{
            "@type": "FAQPage",
            "@id": `${SITE_URL}/#faq`,
            mainEntity: home.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          }]
        : [])),
    },
    ...(ru
      ? [
          {
            path: "/ru",
            lang: "ru",
            locale: "ru_RU",
            alternates: [["en", "/"], ["ru", "/ru"], ["x-default", "/"]],
            title: `Хачик Арутюнян — Senior Full-Stack веб и мобильный разработчик (React, React Native)`,
            description: `Хачик Арутюнян — Senior Full-Stack веб и мобильный разработчик из Еревана, ${years}+ лет опыта. Разрабатываю приложения для iOS и Android на React Native, веб на React и Next.js, бэкенд на Node.js. Фриланс и удалённая работа.`,
            image,
            type: "profile",
            changefreq: "monthly",
            priority: "0.9",
            jsonLd: graph(
              ...base(),
              {
                "@type": "ProfilePage",
                "@id": `${SITE_URL}/ru#profilepage`,
                url: absoluteUrl("/ru"),
                name: `${ru.name} — ${ru.title}`,
                isPartOf: { "@id": websiteId },
                mainEntity: { "@id": personId },
                about: { "@id": personId },
                inLanguage: "ru",
              },
              {
                "@type": "FAQPage",
                "@id": `${SITE_URL}/ru#faq`,
                inLanguage: "ru",
                mainEntity: ru.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
              }
            ),
          },
        ]
      : []),
    {
      path: "/portfolio",
      title: `Portfolio — React & React Native Projects | ${profile.name}`,
      description: `Case studies by ${profile.name}: iOS & Android apps, web platforms, CMS and admin panels built with React Native, React, Next.js and Node.js — Voxa (AI voice notes), Loudey, Altermeliora, Koreez, Bezee and more.`,
      image,
      type: "website",
      changefreq: "monthly",
      priority: "0.9",
      jsonLd: graph(
        ...base(),
        {
          "@type": "CollectionPage",
          url: absoluteUrl("/portfolio"),
          name: `Portfolio of ${profile.name}`,
          isPartOf: { "@id": websiteId },
          about: { "@id": personId },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: projects.map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: absoluteUrl(projectPath(p)),
              name: p.title,
            })),
          },
        },
        breadcrumbs([
          ["Home", "/"],
          ["Portfolio", "/portfolio"],
        ])
      ),
    },
    {
      path: "/resume",
      title: `Resume / CV — ${profile.name}, ${profile.title}`,
      description: `Resume of ${profile.name}: ${years}+ years as a full-stack web & mobile developer — currently ${profile.currentRole}, former Team Lead. React, React Native, Next.js, Node.js. Download the CV as PDF.`,
      image,
      type: "profile",
      changefreq: "monthly",
      priority: "0.9",
      jsonLd: graph(
        ...base(),
        {
          "@type": "ProfilePage",
          url: absoluteUrl("/resume"),
          name: `Resume of ${profile.name}`,
          isPartOf: { "@id": websiteId },
          mainEntity: { "@id": personId },
          associatedMedia: {
            "@type": "MediaObject",
            name: `${profile.name} — CV (PDF)`,
            contentUrl: absoluteUrl(profile.cv.pdf),
            encodingFormat: "application/pdf",
          },
        },
        breadcrumbs([
          ["Home", "/"],
          ["Resume", "/resume"],
        ])
      ),
    },
    {
      path: "/contact",
      title: `Hire ${profile.name} — Web & Mobile Developer | Contact`,
      description: `Need a React, React Native or Next.js developer? Contact ${profile.name} for freelance, contract or full-time remote work. Based in ${profile.location} (${profile.timezone}), working worldwide.`,
      image,
      type: "website",
      changefreq: "yearly",
      priority: "0.7",
      jsonLd: graph(
        ...base(),
        {
          "@type": "ContactPage",
          url: absoluteUrl("/contact"),
          name: `Contact ${profile.name}`,
          isPartOf: { "@id": websiteId },
          mainEntity: { "@id": personId },
        },
        breadcrumbs([
          ["Home", "/"],
          ["Contact", "/contact"],
        ])
      ),
    },
    ...projects.map((p) => {
      const path = projectPath(p);
      const isApp = p.category.includes("mobile");
      const source = (p.links || []).find((l) => /github\.com/.test(l.url));
      const ogImage = `/og/${p.slug}.jpg`;
      return {
        path,
        title: `${p.title} — ${p.description} | ${profile.name}`,
        description: p.summary.length > 160 ? p.summary.slice(0, 157).replace(/\s+\S*$/, "") + "…" : p.summary,
        image: ogImage,
        type: "article",
        changefreq: "yearly",
        priority: "0.8",
        jsonLd: graph(
          ...base(),
          {
            "@type": isApp ? "SoftwareApplication" : "CreativeWork",
            "@id": `${absoluteUrl(path)}#work`,
            name: p.title,
            headline: `${p.title} — ${p.description}`,
            description: p.summary,
            url: absoluteUrl(path),
            image: absoluteUrl(ogImage),
            creator: { "@id": personId },
            author: { "@id": personId },
            dateCreated: p.year,
            keywords: p.technologies.join(", "),
            ...(isApp
              ? {
                  applicationCategory: "MobileApplication",
                  operatingSystem: p.platforms.filter((x) => x !== "Web").join(", "),
                }
              : {}),
            sameAs: (p.links || []).map((l) => l.url),
          },
          ...(source
            ? [{
                "@type": "SoftwareSourceCode",
                name: `${p.title} source code`,
                codeRepository: source.url,
                programmingLanguage: "TypeScript",
                runtimePlatform: p.technologies.slice(0, 3).join(", "),
                author: { "@id": personId },
                targetProduct: { "@id": `${absoluteUrl(path)}#work` },
              }]
            : []),
          breadcrumbs([
            ["Home", "/"],
            ["Portfolio", "/portfolio"],
            [p.title, path],
          ])
        ),
      };
    }),
  ];

  return pages.map((page) => ({ ...page, keywords: KEYWORDS.join(", ") }));
}

export const notFoundPage = (profile) => ({
  path: "/page-not-found",
  title: `Page not found | ${profile.name}`,
  description: `This page doesn't exist. Visit the portfolio of ${profile.name}, ${profile.title}.`,
  image: profile.ogImage,
  type: "website",
  noindex: true,
});
